/**
 * Products: the catalogue.
 *
 * WHY THIS SERVICE EXISTS NOW
 * ---------------------------
 * There was no product write path — only `productReader`. Sync cannot be proven for
 * a record nothing can create, so this is the minimum write service the wiring
 * needs.
 *
 * PRODUCTS SYNC, AND THAT IS NOT A SMALL THING
 * ---------------------------------------------
 * A product is the row that makes an invoice reproducible on another device: its
 * tax rate, its HSN, its unit and its precision all appear on the printed tax
 * invoice. An invoice that arrives without its product would still total
 * correctly and still reconcile, while naming an item the other device has never
 * heard of — which is the kind of wrong that surfaces months later, in an audit.
 *
 * DELETION IS A TOMBSTONE
 * -----------------------
 * `deleteProduct` deactivates and publishes a DELETE. The row stays, for the same
 * reason parties do: a historical invoice must still describe the item it was
 * sold for, and a hard delete would be resurrected by any offline device.
 */
import type { Table } from 'dexie';
import { Dexie } from 'dexie';
import { ulid } from '@platform/ids';
import type { Product, SyncOutboxEntry } from '../db/types';
import { recordBusinessMutation, nextVersion } from '../sync/recordBusinessMutation';
import {
  hsnField,
  productNameField,
  skuField,
  unitField,
} from '@features/masterData/masterDataRules';

export interface ProductTx {
  products: Table<Product, string>;
  syncOutbox: Table<SyncOutboxEntry, string>;
  table(name: string): Table<Record<string, unknown>, string>;
}

export interface CreateProductInput {
  readonly businessId: string;
  readonly name: string;
  readonly kind?: Product['kind'];
  readonly sku?: string;
  readonly categoryId?: string;
  readonly taxRateId?: string;
  readonly hsnSac?: string;
  readonly unit?: string;
  readonly unitPrecision?: number;
  readonly conversionNumerator?: number;
  readonly conversionDenominator?: number;
  readonly salePricePaise?: number;
  readonly purchasePricePaise?: number;
  readonly cessRateBp?: number;
  readonly isStockTracked?: boolean;
  readonly isActive?: boolean;
  readonly user: { readonly id: string; name: string };
  readonly now: string;
  readonly deviceId: string;
}

export interface UpdateProductInput extends Partial<CreateProductInput> {
  readonly id: string;
  readonly businessId: string;
  readonly now: string;
  readonly deviceId: string;
}

export type ProductErrorCode =
  | 'UNKNOWN_PRODUCT'
  | 'WRONG_BUSINESS'
  | 'NAME_REQUIRED'
  | 'INVALID_SKU'
  | 'INVALID_HSN'
  | 'INVALID_UNIT'
  | 'INVALID_PRICE'
  | 'INVALID_TAX_RATE'
  | 'DUPLICATE_SKU';

function fail(code: ProductErrorCode, message: string): Error & { code: ProductErrorCode } {
  return Object.assign(new Error(message), { code, name: 'ProductServiceError' });
}

/**
 * The field rules, run against the values AS TYPED.
 *
 * Defined once in `masterDataRules` and called from both the form (per keystroke)
 * and here (on submit). The form is a suggestion; the service is the rule.
 */
function validateProductFields(input: {
  readonly name?: string;
  readonly sku?: string;
  readonly hsnSac?: string;
  readonly unit?: string;
  readonly salePricePaise?: number;
  readonly purchasePricePaise?: number;
  readonly cessRateBp?: number;
  readonly unitPrecision?: number;
}) {
  const name = productNameField(input.name ?? '');
  if (!name.ok) throw fail('NAME_REQUIRED', name.message ?? 'An item needs a name.');

  const sku = skuField(input.sku ?? '');
  if (!sku.ok) throw fail('INVALID_SKU', sku.message ?? 'That item code is not valid.');

  const hsn = hsnField(input.hsnSac ?? '');
  if (!hsn.ok) throw fail('INVALID_HSN', hsn.message ?? 'That HSN/SAC is not valid.');

  const unit = unitField(input.unit ?? '');
  if (!unit.ok) throw fail('INVALID_UNIT', unit.message ?? 'An item needs a unit.');

  // Prices are integer paise, so the domain check is "a whole number of paise".
  // Negative is refused rather than accepted: a catalogue price that owes the
  // customer money is not a price, and the invoice engine would flag it later
  // where the shopkeeper cannot see why.
  for (const [value, label] of [
    [input.salePricePaise, 'Selling price'],
    [input.purchasePricePaise, 'Purchase price'],
  ] as const) {
    if (value === undefined) continue;
    if (!Number.isInteger(value) || value < 0) {
      throw fail('INVALID_PRICE', `${label} cannot be negative.`);
    }
  }

  // Cess is in basis points, 0 to 5000 (50%). Above that it is not a cess rate,
  // and a tax document carrying one is arithmetic nobody can audit.
  if (input.cessRateBp !== undefined) {
    if (!Number.isInteger(input.cessRateBp) || input.cessRateBp < 0 || input.cessRateBp > 5000) {
      throw fail('INVALID_TAX_RATE', 'Cess must be between 0 and 5000 (0% to 50%).');
    }
  }
  if (input.unitPrecision !== undefined) {
    if (!Number.isInteger(input.unitPrecision) || input.unitPrecision < 0 || input.unitPrecision > 6) {
      throw fail('INVALID_TAX_RATE', 'Decimal places must be between 0 and 6.');
    }
  }
}

/**
 * Whether another ACTIVE product in this company already claims this SKU.
 *
 * Same reasoning as the party GSTIN, and no more than that: a SKU is the shop's
 * own label for an item, and two live items sharing one means a barcode scanner
 * cannot tell them apart and the picker cannot either. Inactive rows are excluded
 * so a re-activated item does not collide with its own tombstone, and `ignoreId`
 * lets an unchanged product save itself.
 *
 * NOT `async`, for the same reason as `findDuplicateGstin`: this runs inside a
 * live Dexie transaction, and an `async` wrapper returns a promise Dexie cannot
 * see, so it can conclude the transaction is finished while the write is still in
 * flight — committing the row with the outbox empty.
 */
export function findDuplicateSku(
  tx: Pick<ProductTx, 'products'>,
  input: { readonly businessId: string; readonly sku?: string; readonly ignoreId?: string },
): Dexie.Promise<Product | null> {
  const sku = input.sku?.trim().toLowerCase();
  if (!sku) return Dexie.Promise.resolve(null);
  return tx.products
    .where('businessId')
    .equals(input.businessId)
    .toArray()
    .then((rows) =>
      rows.find(
        (product) =>
          product.id !== input.ignoreId &&
          product.isActive &&
          product.sku?.trim().toLowerCase() === sku,
      ) ?? null,
    );
}

/**
 * Create a product, and record the mutation in the SAME transaction.
 *
 * THE CHANGE ID IS MINTED, NOT DERIVED
 * -----------------------------------
 * `recordBusinessMutation` generates a fresh ULID per call. A DERIVED id —
 * `product:<id>` — can be reconstructed by another device from the row it already
 * holds, so two devices mint the same change id for the same event and the
 * provider, which keys idempotency on `changeId` alone, collapses the second as a
 * duplicate: the item is created on one device and silently never reaches the
 * other. See `docs/sync-architecture.md`.
 */
export function createProduct(
  tx: ProductTx,
  input: CreateProductInput,
): Dexie.Promise<Product> {
  // Validated against the EFFECTIVE values, not the raw input. `unit` has always
  // defaulted to 'NOS' below, so a caller that omits it still produces a valid row
  // — and checking the raw input would refuse a write this service has always
  // accepted. The rule must run on the row that will actually be written.
  const effective = {
    ...input,
    unit: input.unit ?? 'NOS',
  };
  validateProductFields(effective);
  return findDuplicateSku(tx, {
    businessId: input.businessId,
    sku: input.sku,
  }).then((duplicate) => {
    if (duplicate) {
      throw fail(
        'DUPLICATE_SKU',
        `Item code ${input.sku?.trim()} is already used by ${duplicate.name}. ` +
          'Two items cannot share one code, because a barcode scanner cannot tell them apart.',
      );
    }
    return writeProduct(tx, effective);
  });
}

/**
 * The actual insert, once the duplicate question is answered.
 *
 * Split out so the whole of `createProduct` is ONE promise chain Dexie can follow.
 * An `async` function returns a promise Dexie cannot see, so it can conclude the
 * transaction is finished while `recordBusinessMutation` is still in flight — and
 * the row then commits with an EMPTY outbox.
 */
function writeProduct(tx: ProductTx, input: CreateProductInput): Dexie.Promise<Product> {
  const id = ulid();
  const product = {
    id,
    businessId: input.businessId,
    name: input.name.trim(),
    kind: input.kind ?? 'GOODS',
    sku: input.sku,
    categoryId: input.categoryId,
    taxRateId: input.taxRateId,
    hsnSac: input.hsnSac,
    unit: input.unit ?? 'NOS',
    unitPrecision: input.unitPrecision ?? 0,
    // A conversion of 1:1 is the identity, and is stored explicitly rather than
    // left undefined: a receiving device must not have to guess that a missing
    // numerator means one.
    conversionNumerator: input.conversionNumerator ?? 1,
    conversionDenominator: input.conversionDenominator ?? 1,
    salePricePaise: input.salePricePaise ?? 0,
    purchasePricePaise: input.purchasePricePaise ?? 0,
    cessRateBp: input.cessRateBp ?? 0,
    isStockTracked: input.isStockTracked ?? true,
    isActive: input.isActive ?? true,
    createdAt: input.now,
    createdBy: input.user.id,
    updatedAt: input.now,
    updatedBy: input.user.id,
    syncVersion: 1,
  } as unknown as Product;

  return tx.products.put(product).then(() =>
    recordBusinessMutation(tx, {
    businessId: input.businessId,
    deviceId: input.deviceId,
    entityType: 'product',
    entityId: id,
    operation: 'UPSERT',
    version: 1,
    changedAt: input.now,
    payload: product as unknown as Record<string, unknown>,
    }).then(() => product),
  );
}

/**
 * Edit a product, and record the mutation in the same transaction.
 *
 * The version is read from the ROW and incremented, so two devices editing the
 * same item both produce version 2 and `conflictModel.ts` can ask the question
 * that matters — did they touch different fields, or the same one? A clock would
 * answer "whoever's laptop woke up last", which is not a business rule.
 */
export function updateProduct(
  tx: ProductTx,
  input: UpdateProductInput,
): Dexie.Promise<Product> {
  return tx.products.get(input.id).then((existing) => {
  if (!existing) {
    throw fail('UNKNOWN_PRODUCT', `No item with id "${input.id}".`);
  }
  if (existing.businessId !== input.businessId) {
    throw fail(
      'WRONG_BUSINESS',
      'That item belongs to a different company. Refusing, because editing it here would ' +
        "change another business's catalogue.",
    );
  }

  const version = nextVersion(existing.syncVersion);
  // Validated on the MERGED result, so an edit that changes only the price is
  // still checked for negative money, and a caller sending a partial update
  // cannot smuggle an invalid GSTIN-equivalent past by omission.
  const mergedInput = {
    name: input.name?.trim() ?? existing.name,
    sku: input.sku ?? existing.sku,
    hsnSac: input.hsnSac ?? existing.hsnSac,
    unit: input.unit ?? existing.unit,
    salePricePaise: input.salePricePaise ?? existing.salePricePaise,
    purchasePricePaise: input.purchasePricePaise ?? existing.purchasePricePaise,
    cessRateBp: input.cessRateBp ?? existing.cessRateBp,
    unitPrecision: input.unitPrecision ?? existing.unitPrecision,
  };
  validateProductFields(mergedInput);

    return findDuplicateSku(tx, {
      businessId: input.businessId,
      sku: mergedInput.sku,
      // Ignoring this row is what lets an unchanged item save itself.
      ignoreId: existing.id,
    }).then((duplicate) => {
      if (duplicate) {
        throw fail(
          'DUPLICATE_SKU',
          `Item code ${mergedInput.sku?.trim()} is already used by ${duplicate.name}. ` +
            'Two items cannot share one code, because a barcode scanner cannot tell them apart.',
        );
      }

      const merged = {
        ...existing,
        name: input.name?.trim() ?? existing.name,
        sku: input.sku ?? existing.sku,
        categoryId: input.categoryId ?? existing.categoryId,
        taxRateId: input.taxRateId ?? existing.taxRateId,
        hsnSac: input.hsnSac ?? existing.hsnSac,
        unit: input.unit ?? existing.unit,
        unitPrecision: input.unitPrecision ?? existing.unitPrecision,
        salePricePaise: input.salePricePaise ?? existing.salePricePaise,
        purchasePricePaise: input.purchasePricePaise ?? existing.purchasePricePaise,
        cessRateBp: input.cessRateBp ?? existing.cessRateBp,
        isStockTracked: input.isStockTracked ?? existing.isStockTracked,
        isActive: input.isActive ?? existing.isActive,
        updatedAt: input.now,
        updatedBy: input.user?.id ?? existing.updatedBy,
        syncVersion: version,
      } as Product;

      return tx.products.put(merged).then(() =>
        recordBusinessMutation(tx, {
          businessId: input.businessId,
          deviceId: input.deviceId,
          entityType: 'product',
          entityId: input.id,
          operation: 'UPSERT',
          version,
          changedAt: input.now,
          payload: merged as unknown as Record<string, unknown>,
        }).then(() => merged),
      );
    });
  });
}

/**
 * Deactivate a product and publish a TOMBSTONE. The row stays — see the file
 * header: a historical invoice must still describe the item it was sold for.
 */
export function deleteProduct(
  tx: ProductTx,
  input: { id: string; businessId: string; now: string; deviceId: string },
): Dexie.Promise<void> {
  return tx.products.get(input.id).then((existing) => {
    if (!existing) return undefined;
    if (existing.businessId !== input.businessId) {
      throw fail('WRONG_BUSINESS', 'That item belongs to a different company.');
    }

    const version = nextVersion(existing.syncVersion);
    return tx.products
      .put({ ...existing, isActive: false, syncVersion: version } as Product)
      .then(() =>
        recordBusinessMutation(tx, {
          businessId: input.businessId,
          deviceId: input.deviceId,
          entityType: 'product',
          entityId: input.id,
          operation: 'DELETE',
          version,
          changedAt: input.now,
          deletedAt: input.now,
        }).then(() => undefined),
      );
  });
}

